import { request } from './client'
import { asObject, num, optNum, str, text, time } from './parse'
import type { Money } from './threads'

/**
 * One recorded call to a model.
 *
 * Deliberately thin: no message text and no model output. The words live in
 * `messages`, which is what keeps a client's personal data in one place and an
 * erasure request answerable (invariant 10). This screen answers "which step,
 * which model, what did it cost", not "what was said".
 */
export interface AIRun {
  readonly id: string
  readonly task: string
  readonly provider: string
  readonly model: string
  readonly promptVersion: string
  readonly schemaVersion: string
  readonly tokens: { readonly input: number; readonly output: number; readonly cacheRead: number } | null
  readonly cost: Money | undefined
  readonly latencyMs: number | undefined
  readonly status: string
  readonly error: string
  readonly createdAt: string
}

export function fetchAIRun(runId: string, signal?: AbortSignal): Promise<AIRun> {
  return request(
    `/api/ai-runs/${encodeURIComponent(runId)}`,
    (body) => {
      const source = asObject(body, 'the run')
      const tokens = source['tokens']
      const cost = source['cost']

      return {
        id: str(source, 'id'),
        task: text(source, 'task'),
        provider: text(source, 'provider'),
        model: text(source, 'model'),
        promptVersion: text(source, 'prompt_version'),
        schemaVersion: text(source, 'schema_version'),
        // Null rather than three zeroes: a provider that reported no usage is
        // not a call that used no tokens, and a cost limit reading it as zero
        // would be passed silently.
        tokens:
          tokens === undefined || tokens === null
            ? null
            : (() => {
                const usage = asObject(tokens, '"tokens"')
                return {
                  input: num(usage, 'input'),
                  output: num(usage, 'output'),
                  cacheRead: optNum(usage, 'cache_read') ?? 0,
                }
              })(),
        cost:
          cost === undefined || cost === null
            ? undefined
            : (() => {
                const amount = asObject(cost, '"cost"')
                const minor = amount['amount_minor']
                return {
                  amountMinor: typeof minor === 'number' ? minor : null,
                  currency: str(amount, 'currency'),
                  kind: str(amount, 'kind'),
                }
              })(),
        latencyMs: optNum(source, 'latency_ms'),
        status: text(source, 'status'),
        error: text(source, 'error'),
        createdAt: time(source, 'created_at'),
      }
    },
    signal === undefined ? {} : { signal },
  )
}
